import classNames from 'classnames';
import React, { MouseEvent, useState } from 'react';

import { InfoCircle, SmilieHappy, SmilieSad } from '@zooplus/icons';

import { getAdobeAccountEventTracking } from '../../../services/trackingService';
import { PspConnector, RetryPaymentMethod } from '../../../types';
import { hasText } from '../../../utils/utils';
import RetryPaymentBraintreeModal from '../payment/RetryPaymentBraintreeModal';

import styles from './Notification.module.scss';

export type NotificationProps = {
  type?: 'success' | 'warning' | 'info';
  id: string;
  ariaLabel: string;
  linkText?: string;
  linkUrl?: string;
  linkDataZta?: string;
  orderId?: string;
  retryPaymentMethod?: RetryPaymentMethod;
  connector?: PspConnector;
  pspToken?: string;
  amount?: number;
  currency?: string;
  siteId?: number;
};

const Notification: React.FC<NotificationProps> = ({
  type = 'info',
  id,
  ariaLabel,
  children,
  linkText,
  linkUrl,
  linkDataZta,
  retryPaymentMethod,
  orderId,
  connector,
  pspToken,
  amount,
  currency,
  siteId,
}) => {
  const [showBraintreeFormModal, setShowBraintreeFormModal] = useState(false);

  const sendAdobeTrackingClickEvent = () => {
    const adbAccEvent = getAdobeAccountEventTracking();
    adbAccEvent.track('retry_payment', {
      payment_method: retryPaymentMethod,
      order_id: orderId,
    });
  };

  const handleOnClick = (e: MouseEvent<HTMLElement>) => {
    sendAdobeTrackingClickEvent();

    if (connector?.toUpperCase() === 'BRAINTREE') {
      setShowBraintreeFormModal(!showBraintreeFormModal);
      e.preventDefault();
    }
  };

  return (
    <>
      <div
        className={classNames(styles.notification, `v3-notification v3-notification--${type}`)}
        role="alertdialog"
        data-zta={id}
        aria-labelledby={ariaLabel}
        aria-describedby={id}
        aria-label={ariaLabel}>
        <div className="v3-notification__body">
          <i className={`v3-notification__icon--${type}`}>
            {type === 'warning' && <SmilieSad data-zta="smilie-sad" />}
            {type === 'success' && <SmilieHappy data-zta="smilie-happy" />}
            {type === 'info' && <InfoCircle data-zta="info-circle" />}
          </i>
          <div id={id} className="v3-notification__text">
            {children}
          </div>
          {hasText(linkText) && (
            <a
              href={linkUrl}
              className={classNames('v3-notification__btn')}
              data-zta={linkDataZta}
              onClick={handleOnClick}>
              {linkText}
            </a>
          )}
        </div>
        {hasText(linkText) && (
          <a
            href={linkUrl}
            className={classNames('v3-notification__btnMobile')}
            data-zta={linkDataZta}
            onClick={handleOnClick}>
            {linkText}
          </a>
        )}
      </div>

      {connector === 'BRAINTREE' && pspToken && amount && currency && orderId && siteId && (
        <RetryPaymentBraintreeModal
          show={showBraintreeFormModal}
          token={pspToken}
          onClose={() => setShowBraintreeFormModal(false)}
          amount={amount}
          currency={currency}
          orderId={orderId}
          siteId={siteId}
        />
      )}
    </>
  );
};

export default Notification;
